// src/pages/settings/Settings.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Tabs,
  Tab,
  Divider,
  Paper,
  useTheme,
  alpha,
  Grid,
  Box,
  Typography
} from '@mui/material';

// Import styled components
import {
  PageContainer,
  SectionContainer,
  SectionTitle,
  TabPanel,
  FlexBox,
  FlexBetween,
  TruncatedText,
  PaperCard
} from '../../components/ui';
import {
  PersonOutline as UserIcon,
  SettingsOutlined as GeneralIcon,
  NotificationsOutlined as NotificationIcon,
  PaletteOutlined as AppearanceIcon,
  SecurityOutlined as SecurityIcon,
  BusinessOutlined as ClientIcon,
  AdminPanelSettingsOutlined as AdminIcon,
  Storage as DataIcon,
} from '@mui/icons-material';

// Import settings components

// Import stores
import useSettingsStore from '../../stores/settingsStore';
import useAuthStore from '../../stores/authStore';
import UserSettings from './components/UserSettings';
import AppearanceSettings from './components/AppearanceSettings';
import NotificationSettings from './components/NotificationSettings';
import SecuritySettings from './components/SecuritySettings';
import ClientSettings from './components/ClientSettings';
import SystemSettings from './components/SystemSettings';
import DataManagement from './DataManagement';

// We're now using the TabPanel component from our UI components

// Settings page component
const Settings = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isSuperAdmin: checkIsSuperAdmin } = useAuthStore();
  
  // Use the auth store's isSuperAdmin method which properly checks roleId
  const isSuperAdmin = checkIsSuperAdmin();
  
  // Check for admin role using roleId
  const isAdmin = user && user.roleId && ['superadmin', 'admin'].includes(user.roleId.key);
  
  const isLoading = useSettingsStore(state => state.isLoading);
  
  // Get tab from URL or default to 'user'
  const getTabFromUrl = () => {
    const tabParam = new URLSearchParams(location.search).get('tab');
    switch (tabParam) {
      case 'appearance':
        return 1;
      case 'notifications':
        return 2;
      case 'security':
        return 3;
      case 'clients':
        return 4;
      case 'system':
        return 5;
      case 'data':
        return 6;
      default:
        return 0; // Default to user tab
    }
  };
  
  const [activeTab, setActiveTab] = useState(getTabFromUrl());
  
  // Debug logging
  console.log('🔍 Settings component rendering:', {
    userRole: user?.roleId?.key,
    userRoleName: user?.roleId?.name,
    isAdmin,
    isSuperAdmin,
    activeTab,
    hasUser: !!user,
    userObject: user
  });
  
  // Update URL when tab changes
  useEffect(() => {
    const tabNames = ['user', 'appearance', 'notifications', 'security', 'clients', 'system', 'data'];
    const tabName = tabNames[activeTab];
    
    navigate(`/settings?tab=${tabName}`, { replace: true });
  }, [activeTab]); // Remove navigate from dependencies
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  return (
    <PageContainer>
      {/* Header */}
      <SectionContainer>
        <SectionTitle>Settings</SectionTitle>
        <TruncatedText variant="body1" color="textSecondary">
          Manage your account preferences and system settings
        </TruncatedText>
      </SectionContainer>
      
      {/* Tabs and Content */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <PaperCard>
            <Tabs
              orientation="vertical"
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                // borderRight: 1,
                // borderColor: theme.palette.divider,
                '& .MuiTab-root': {
                  // ...theme.aghrba.tokens.layout.flex.row,
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  // pl: 3,
                  // py: 2,
                  // ...theme.aghrba.tokens.text.tabLabel,
                  // transition: theme.aghrba.tokens.transitions.standard,
                },
                '& .Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, theme.aghrba.tokens.alpha.soft),
                  borderRight: `3px solid ${theme.palette.primary.main}`,
                }
              }}
            >
              <Tab
                icon={<FlexBox sx={{ mr: 1 }}><UserIcon /></FlexBox>}
                iconPosition="start"
                label="User Profile"
              />
              <Tab
                icon={<FlexBox sx={{ mr: 1 }}><AppearanceIcon /></FlexBox>}
                iconPosition="start"
                label="Appearance"
              />
              <Tab
                icon={<FlexBox sx={{ mr: 1 }}><NotificationIcon /></FlexBox>}
                iconPosition="start"
                label="Notifications"
              />
              <Tab
                icon={<FlexBox sx={{ mr: 1 }}><SecurityIcon /></FlexBox>}
                iconPosition="start"
                label="Security"
              />
              {isAdmin && (
                <Tab
                  icon={<FlexBox sx={{ mr: 1 }}><ClientIcon /></FlexBox>}
                  iconPosition="start"
                  label="Client Settings"
                />
              )}
              {isAdmin && (
                <Tab
                  icon={<FlexBox sx={{ mr: 1 }}><AdminIcon /></FlexBox>}
                  iconPosition="start"
                  label="System Settings"
                />
              )}
              {isSuperAdmin && (
                <Tab
                  icon={<FlexBox sx={{ mr: 1 }}><DataIcon /></FlexBox>}
                  iconPosition="start"
                  label="Data Management"
                />
              )}
            </Tabs>
          </PaperCard>
        </Grid>
        
        <Grid item xs={12} md={9}>
          <PaperCard sx={{ p: 3 }}>
            <TabPanel value={activeTab} index={0}>
              <UserSettings />
            </TabPanel>
            
            <TabPanel value={activeTab} index={1}>
              <AppearanceSettings />
            </TabPanel>
            
            <TabPanel value={activeTab} index={2}>
              <NotificationSettings />
            </TabPanel>
            
            <TabPanel value={activeTab} index={3}>
              <SecuritySettings />
            </TabPanel>
            
            {isAdmin && (
              <TabPanel value={activeTab} index={4}>
                <ClientSettings />
              </TabPanel>
            )}
            
            {isAdmin && (
              <TabPanel value={activeTab} index={5}>
                <SystemSettings />
              </TabPanel>
            )}
            
            {isSuperAdmin && (
              <TabPanel value={activeTab} index={6}>
                <DataManagement />
              </TabPanel>
            )}
          </PaperCard>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default Settings;